import React, {useEffect, useState} from 'react';

import {SNAKK_CHAT} from './constants';
import {HideSnakkChat, SnakkChatUnavailable, SnakkChatWelcomeOnHover} from './styled';

interface SnakkChatProps {
	active: boolean;
}

const CONTAINER_SELECTOR = '.snakk-chat-container';
const WELCOME_TITLE_SELECTOR = '.snakk-chat-welcome-title';

// Returns whether the chat is unavailable, or null while the widget hasn't rendered yet
const readUnavailable = (): boolean | null => {
	const title = document.querySelector(`${CONTAINER_SELECTOR} ${WELCOME_TITLE_SELECTOR}`)?.textContent?.trim();
	if (title === undefined) return null;
	return SNAKK_CHAT.UNAVAILABLE_TITLES.includes(title);
};

// Capture phase on the container runs before the launcher's own inline onclick
const blockClick = (event: MouseEvent) => {
	event.preventDefault();
	event.stopPropagation();
};

// Loads the Snakk.ai chat widget the first time it becomes active and hides it while inactive.
// The script is never removed: it cannot be torn down, and loading it twice would mount two widgets.
const SnakkChat: React.FC<SnakkChatProps> = ({active}) => {
	const [unavailable, setUnavailable] = useState<boolean | null>(null);
	const [tooltipSuppressed, setTooltipSuppressed] = useState(false);

	useEffect(() => {
		if (!active || document.getElementById(SNAKK_CHAT.SCRIPT_ID)) return;
		const script = document.createElement('script');
		script.id = SNAKK_CHAT.SCRIPT_ID;
		script.src = SNAKK_CHAT.SCRIPT_URL;
		script.async = true;
		script.dataset['agentId'] = SNAKK_CHAT.AGENT_ID;
		script.onerror = () => console.error('Snakk chat: failed to load embed script');
		document.body.appendChild(script);
	}, [active]);

	// The widget renders after fetching its config, so wait for its welcome title to know if it can be used
	useEffect(() => {
		if (!active || unavailable !== null) return;
		const initial = readUnavailable();
		if (initial !== null) {
			setUnavailable(initial);
			return;
		}
		const observer = new MutationObserver(() => {
			const result = readUnavailable();
			if (result === null) return;
			observer.disconnect();
			setUnavailable(result);
		});
		observer.observe(document.body, {childList: true, subtree: true});
		return () => observer.disconnect();
	}, [active, unavailable]);

	useEffect(() => {
		if (!unavailable) return;
		const container = document.querySelector<HTMLElement>(CONTAINER_SELECTOR);
		container?.addEventListener('click', blockClick, true);
		return () => container?.removeEventListener('click', blockClick, true);
	}, [unavailable]);

	// Opening or closing the chat is a click with the mouse still over the launcher; hide the tooltip until it leaves
	useEffect(() => {
		if (unavailable === null) return;
		const container = document.querySelector<HTMLElement>(CONTAINER_SELECTOR);
		if (!container) return;
		const suppress = () => setTooltipSuppressed(true);
		const release = () => setTooltipSuppressed(false);
		container.addEventListener('click', suppress);
		container.addEventListener('mouseleave', release);
		return () => {
			container.removeEventListener('click', suppress);
			container.removeEventListener('mouseleave', release);
		};
	}, [unavailable]);

	if (!active) return <HideSnakkChat />;
	return (
		<>
			<SnakkChatWelcomeOnHover $suppressed={tooltipSuppressed} />
			{unavailable && <SnakkChatUnavailable />}
		</>
	);
};

export default SnakkChat;
